import React, { useState } from "react";
import { redirectToPayFast } from "../utils/payfast";
import "./Donate.css";

const IconGeneral = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

const IconFeeding = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 8h1a4 4 0 010 8h-1" />
    <path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z" />
    <line x1="6" y1="1" x2="6" y2="4" />
    <line x1="10" y1="1" x2="10" y2="4" />
    <line x1="14" y1="1" x2="14" y2="4" />
  </svg>
);

const IconSchool = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" />
    <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
  </svg>
);

const IconECD = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);

const IconHomeCare = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
  </svg>
);

const IconHousing = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const causes = [
  {
    id: "general",
    title: "General Fund",
    desc: "Support wherever the need is greatest — meals, education, care, and housing for the most vulnerable.",
    impact:
      "Your donation is directed to the highest-priority need at the time of giving.",
    Icon: IconGeneral,
    payfastItem: "General Donation — Food and Nutrition Foundation",
  },
  {
    id: "feeding",
    title: "Feeding Programme",
    desc: "Fund nutritious meals for children and families in Kamanga Compound, four times a week.",
    impact: "R100 feeds a child for two full weeks.",
    Icon: IconFeeding,
    payfastItem: "Feeding Programme — Food and Nutrition Foundation",
  },
  {
    id: "school",
    title: "School Sponsorship",
    desc: "Pay school fees, supply uniforms and stationery, and keep a child in education.",
    impact: "R500 covers a full month of school fees for one child.",
    Icon: IconSchool,
    payfastItem: "School Sponsorship — Food and Nutrition Foundation",
  },
  {
    id: "ecd",
    title: "Early Childhood Development",
    desc: "Fund our community preschool for children aged 3–6, giving them the best start before Grade 1.",
    impact: "R600 covers a full term of preschool for one child.",
    Icon: IconECD,
    payfastItem: "Early Childhood Development — Food and Nutrition Foundation",
  },
  {
    id: "homecare",
    title: "Home-Based Care",
    desc: "Enable ten caregivers to visit people living with HIV/AIDS — food hampers, first aid, and dignity.",
    impact: "R300 provides a food hamper and hygiene supplies for one family.",
    Icon: IconHomeCare,
    payfastItem: "Home-Based Care — Food and Nutrition Foundation",
  },
  {
    id: "housing",
    title: "Housing Programme",
    desc: "Help build a house for a widow or grandmother raising orphaned grandchildren in unsafe conditions.",
    impact: "R2,000 contributes meaningfully to a single housing build.",
    Icon: IconHousing,
    payfastItem: "Housing Programme — Food and Nutrition Foundation",
  },
];

const suggestedAmounts = [100, 200, 500, 1000, 2500, 5000];

export default function Donate() {
  const [selectedCause, setSelectedCause] = useState(causes[0]);
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [frequency, setFrequency] = useState("once");
  const [error, setError] = useState("");

  const finalAmount = amount === "custom" ? customAmount : amount;

  const handleAmountSelect = (val) => {
    setAmount(String(val));
    setCustomAmount("");
    setError("");
  };

  const handleCustomChange = (e) => {
    setAmount("custom");
    setCustomAmount(e.target.value);
    setError("");
  };

  const handleDonate = () => {
    const num = parseFloat(finalAmount);
    if (!finalAmount || isNaN(num) || num < 10) {
      setError("Please enter a valid amount of at least R10.");
      return;
    }
    redirectToPayFast({ amount: finalAmount, cause: selectedCause, frequency });
  };

  return (
    <main className="donate-page">
      <section className="page-hero" aria-label="Donate page hero">
        <div
          className="page-hero-bg"
          style={{
            backgroundImage: "url('/images/gallery/smiling-children-01.jpg')",
          }}
        />
        <div className="container page-hero-content">
          <span className="eyebrow" style={{ color: "var(--amber-light)" }}>
            Make a Difference
          </span>
          <h1>Donate to the Food and Nutrition Foundation</h1>
          <p>
            Your donation goes directly to one of our seven programmes, serving
            Zambia's most vulnerable children, mothers, and families.
          </p>
        </div>
      </section>

      <section className="section donate-section">
        <div className="container">
          <div className="donate-layout">
            {/* LEFT: CAUSE SELECTOR */}
            <div className="donate-causes">
              <h2 className="donate-section-heading">Choose a Cause</h2>
              <p className="donate-section-sub">
                Select the programme you want to support. Your donation goes
                directly to that cause.
              </p>
              <div className="cause-list">
                {causes.map((cause) => (
                  <button
                    key={cause.id}
                    className={`cause-card${selectedCause.id === cause.id ? " selected" : ""}`}
                    onClick={() => setSelectedCause(cause)}
                    aria-pressed={selectedCause.id === cause.id}
                  >
                    <span className="cause-icon">
                      <cause.Icon />
                    </span>
                    <div className="cause-info">
                      <strong>{cause.title}</strong>
                      <span>{cause.desc}</span>
                    </div>
                    <div
                      className={`cause-check${selectedCause.id === cause.id ? " visible" : ""}`}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* RIGHT: AMOUNT + CHECKOUT */}
            <div className="donate-form-panel">
              <div className="donate-form-card">
                <div className="donate-form-header">
                  <span className="donate-form-cause-icon">
                    <selectedCause.Icon />
                  </span>
                  <div>
                    <h3>{selectedCause.title}</h3>
                    <p className="donate-form-impact">{selectedCause.impact}</p>
                  </div>
                </div>

                <div className="donate-frequency">
                  <button
                    className={`freq-btn${frequency === "once" ? " active" : ""}`}
                    onClick={() => setFrequency("once")}
                  >
                    Give Once
                  </button>
                  <button
                    className={`freq-btn${frequency === "monthly" ? " active" : ""}`}
                    onClick={() => setFrequency("monthly")}
                  >
                    Give Monthly
                  </button>
                </div>

                <div className="donate-amounts">
                  <p className="donate-amounts-label">Select Amount (ZAR)</p>
                  <div className="amount-grid">
                    {suggestedAmounts.map((a) => (
                      <button
                        key={a}
                        className={`amount-btn${String(a) === amount ? " selected" : ""}`}
                        onClick={() => handleAmountSelect(a)}
                      >
                        R{a.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <div className="custom-amount-wrap">
                    <span className="custom-amount-prefix">R</span>
                    <input
                      type="number"
                      min="10"
                      placeholder="Other amount"
                      className={`form-control custom-amount-input${error ? " error" : ""}`}
                      value={customAmount}
                      onChange={handleCustomChange}
                      aria-label="Custom donation amount"
                    />
                  </div>
                  {error && (
                    <p className="error-msg" role="alert">
                      {error}
                    </p>
                  )}
                </div>

                {finalAmount && parseFloat(finalAmount) >= 10 && (
                  <div className="donate-summary">
                    <p>
                      You are giving{" "}
                      <strong>
                        R{parseFloat(finalAmount).toLocaleString()}
                      </strong>
                      {frequency === "monthly" ? " per month " : " once "}
                      to <strong>{selectedCause.title}</strong>.
                    </p>
                  </div>
                )}

                <button
                  className="btn btn-primary donate-submit-btn"
                  onClick={handleDonate}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  Proceed to Secure Payment
                </button>

                <div className="payfast-trust">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--green)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span>
                    Secured by PayFast — South Africa's most trusted payment
                    gateway
                  </span>
                </div>
                <p className="payfast-note">
                  You'll be redirected to the official PayFast payment page,
                  where you can pay by card, Apple Pay, Google Pay, Instant EFT,
                  SnapScan, or Zapper.
                </p>
              </div>

              {/* IMPACT BREAKDOWN */}
              <div className="impact-breakdown">
                <h4>What your gift does</h4>
                <ul>
                  <li>
                    <span className="amount-tag">R50</span>
                    <span>Funds 4 meals for a child</span>
                  </li>
                  <li>
                    <span className="amount-tag">R100</span>
                    <span>Feeds a child for a week</span>
                  </li>
                  <li>
                    <span className="amount-tag">R500</span>
                    <span>Covers a month of school fees</span>
                  </li>
                  <li>
                    <span className="amount-tag">R1,000</span>
                    <span>Hygiene supplies for five families</span>
                  </li>
                  <li>
                    <span className="amount-tag">R1,200</span>
                    <span>Funds a nutrition workshop for a mother</span>
                  </li>
                  <li>
                    <span className="amount-tag">R2,500</span>
                    <span>Full term of preschool for one child</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST SECTION */}
      <section
        className="section trust-section"
        aria-labelledby="trust-heading"
      >
        <div className="container">
          <div className="section-title">
            <span className="eyebrow">Safe &amp; Trusted</span>
            <h2 id="trust-heading">Your Donation Is In Safe Hands</h2>
            <div className="divider divider-center" />
          </div>
          <div className="trust-grid">
            <div className="trust-card">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--amber)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <h4>Secured by PayFast</h4>
              <p>
                All payments are processed by PayFast by Network — South
                Africa's most recognised and trusted payment gateway. Your card
                details never touch our servers.
              </p>
            </div>
            <div className="trust-card">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--amber)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              <h4>100% Goes to Programmes</h4>
              <p>
                Every rand donated is directed to the programme you select. The
                Foundation operates with full financial accountability and
                transparency.
              </p>
            </div>
            <div className="trust-card">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--amber)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <h4>You Stay In Control</h4>
              <p>
                Choose your cause, your amount, and whether you give once or
                monthly. Cancel or adjust a monthly gift at any time.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
